/// <reference lib="dom" />
import type { CardSheetGestureOptions } from '@mobile/screens/card-details/card-sheet-gesture-types'
import { useEffect } from 'react'

export function useCardSheetGestures({
  progress,
  currentRef,
  dragStartRef,
  distance,
  expandedProgress,
  finishDrag,
}: CardSheetGestureOptions) {
  // На web захватываем pointer-жест: RN PanResponder может принять drag за нажатие.
  useEffect(() => {
    let startY: number | null = null
    let moved = false
    let suppressClick = false
    let lastY = 0
    let lastTime = 0
    let velocity = 0
    let pointerId: number | null = null
    let historyScroller: Element | null = null
    let wheelTimer: ReturnType<typeof setTimeout> | undefined
    let isWheeling = false
    const findHistory = (target: EventTarget | null) =>
      target instanceof Element ? target.closest('[data-testid="card-sheet-history"]') : null
    const down = (event: PointerEvent) => {
      if (event.button !== 0) return

      if (!(event.target instanceof Element)) return

      const isHandle = !!event.target.closest('[data-testid="card-sheet-handle"]')
      historyScroller = isHandle ? null : findHistory(event.target)

      if (!isHandle && !historyScroller) return

      if (pointerId !== null) return
      progress.stopAnimation()
      dragStartRef.current = currentRef.current
      startY = event.clientY
      pointerId = event.pointerId
      lastY = event.clientY
      lastTime = event.timeStamp
      velocity = 0
      moved = false
    }
    const move = (event: PointerEvent) => {
      if (startY === null || event.pointerId !== pointerId) return
      const delta = event.clientY - startY
      const elapsed = event.timeStamp - lastTime

      if (elapsed > 0) velocity = (event.clientY - lastY) / elapsed
      lastY = event.clientY
      lastTime = event.timeStamp

      if (!moved && Math.abs(delta) > 8) {
        // Список отдаёт жест панели только у верхней границы; точка захвата становится началом движения.
        if (
          historyScroller &&
          (historyScroller.scrollTop > 0 || (delta < 0 && currentRef.current <= expandedProgress))
        ) {
          startY = null
          pointerId = null
          return
        }
        moved = true
        startY = event.clientY - (historyScroller ? 0 : delta)
        dragStartRef.current = currentRef.current
      }

      if (!moved) return
      event.preventDefault()
      progress.setValue(
        Math.max(expandedProgress, Math.min(1, dragStartRef.current + (event.clientY - startY) / distance)),
      )
    }
    const up = (event: PointerEvent) => {
      if (startY === null || event.pointerId !== pointerId) return
      startY = null
      pointerId = null

      if (!moved) {
        finishDrag()
        return
      }
      suppressClick = true
      event.preventDefault()

      finishDrag(event.timeStamp - lastTime < 80 ? velocity : 0)
    }
    const click = (event: MouseEvent) => {
      if (!suppressClick) return
      suppressClick = false
      event.preventDefault()
      event.stopImmediatePropagation()
    }
    const cancel = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      startY = null
      pointerId = null
      finishDrag()
    }
    // Колесо мыши на верхней границе списка двигает панель, как перетаскивание.
    const wheel = (event: WheelEvent) => {
      const scroller = findHistory(event.target)

      if (!scroller || scroller.scrollTop > 0 || event.deltaY === 0) return

      if (event.deltaY > 0 && currentRef.current <= expandedProgress) return

      if (!isWheeling) {
        isWheeling = true
        progress.stopAnimation()
        dragStartRef.current = currentRef.current
      }
      event.preventDefault()
      progress.setValue(Math.max(expandedProgress, Math.min(1, currentRef.current - event.deltaY / distance)))
      clearTimeout(wheelTimer)
      wheelTimer = setTimeout(() => {
        isWheeling = false
        finishDrag()
      }, 120)
    }
    window.addEventListener('wheel', wheel, { capture: true, passive: false })
    window.addEventListener('pointerdown', down, true)
    window.addEventListener('pointermove', move, { capture: true, passive: false })
    window.addEventListener('pointerup', up, true)
    window.addEventListener('pointercancel', cancel)
    window.addEventListener('click', click, true)
    return () => {
      window.removeEventListener('wheel', wheel, true)
      clearTimeout(wheelTimer)
      window.removeEventListener('pointerdown', down, true)
      window.removeEventListener('pointermove', move, true)
      window.removeEventListener('pointerup', up, true)
      window.removeEventListener('pointercancel', cancel)
      window.removeEventListener('click', click, true)
    }
  }, [distance, expandedProgress, progress, finishDrag, currentRef, dragStartRef])
}
